/**
 * Button → KMD insertion (KHIYARSA-EDITOR-1 Phase 4, §B1).
 *
 * A button inserts at the caret's LOGICAL position, not at the raw caret. The
 * contract states the rule; the readings it leaves open are settled here, once,
 * so every button behaves the same way:
 *
 *  1. A **sibling** insert (Step, Component, Field, Event, Participant…) lands
 *     on the line AFTER the caret's list item, at that item's indent. With the
 *     caret anywhere else — a heading, a blank line, frontmatter — it appends
 *     to the end of the block the caret sits in.
 *  2. A **child** insert (Branch) lands under the caret's item, indented one
 *     level. Branches are only offered on a `{decision}` line, so this never
 *     has to guess whether the user meant a sibling.
 *  3. A **container** insert (Stage, Lane, Zone, Entity…) always lands at
 *     column 0 at the end of the caret's container block — headings never nest
 *     inside a list.
 *  4. A **document-level** insert (an edge statement) appends at the end of the
 *     document. Edge statements are order-independent by design (spec §3.4
 *     allows forward references), so there is no position to be clever about.
 *  5. Nothing is ever inserted INSIDE frontmatter; a caret parked there is
 *     treated as "before the body".
 *
 * Every insert returns a caret range covering the placeholder text so the user
 * can type over it immediately.
 *
 * Pure and React-free, so the rules are unit-testable without a browser.
 */
import { DIRECTIVES, FAMILY_INFO } from "../vocab";

import type { Family } from "../types";

export interface InsertResult {
  text: string;
  /** selection to leave behind: the placeholder, ready to be typed over */
  selectionStart: number;
  selectionEnd: number;
}

export type ActionId =
  | "container"
  | "subcontainer"
  | "node"
  | "decision"
  | "branch"
  | "start"
  | "end"
  | "child"
  | "field"
  | "event"
  | "edge"
  | "reply"
  | "selfMessage"
  | "relationship"
  | "note"
  | "sectionBreak"
  | "group"
  | "loop"
  | "alt"
  | "opt"
  | "par";

export interface ButtonSpec {
  id: ActionId;
  /** i18n key in editor/strings.ts */
  labelKey: string;
}

// ---------------------------------------------------------------------------
// document analysis
// ---------------------------------------------------------------------------

interface Line {
  index: number;
  text: string;
  start: number;
  end: number;
}

const toLines = (text: string): Line[] => {
  const out: Line[] = [];
  let start = 0;
  text.split("\n").forEach((value, index) => {
    out.push({ index, text: value, start, end: start + value.length });
    start += value.length + 1;
  });
  return out;
};

const lineAt = (lines: Line[], caret: number): Line => {
  for (const line of lines) {
    if (caret <= line.end) {
      return line;
    }
  }
  return lines[lines.length - 1];
};

/** index of the line after the closing `---` of frontmatter, or 0 */
const bodyStartLine = (lines: Line[]): number => {
  if (lines.length === 0 || lines[0].text.trim() !== "---") {
    return 0;
  }
  for (let i = 1; i < lines.length; i++) {
    if (lines[i].text.trim() === "---") {
      return i + 1;
    }
  }
  return 0;
};

const LIST_RE = /^(\s*)(?:[-*+]|\d+\.)\s+(.*)$/;
const HEADING_RE = /^(#{1,6})\s+(.*)$/;

const indentOf = (line: string): string => LIST_RE.exec(line)?.[1] ?? "";

const isListItem = (line: string) => LIST_RE.test(line);
const isHeading = (line: string) => HEADING_RE.test(line);
const isBlank = (line: string) => line.trim() === "";

/**
 * Last line of the "block" the caret sits in — the run of list items and prose
 * belonging to the current heading, stopping before the next heading, a
 * directive fence, or a section break.
 */
const blockEnd = (lines: Line[], from: number): number => {
  let last = from;
  for (let i = from; i < lines.length; i++) {
    const value = lines[i].text;
    if (i > from && (isHeading(value) || value.trim() === "---")) {
      break;
    }
    if (/^\s*:{2,}/.test(value) && i > from) {
      break;
    }
    if (!isBlank(value)) {
      last = i;
    }
  }
  return last;
};

/**
 * Last line of the caret item's SUBTREE — the item plus every more-indented
 * line under it.
 *
 * A "sibling" insert has to land after all of that, not on the line straight
 * after the item. Insert between a `{decision}` and its indented `yes:`/`no:`
 * branches and the branches silently re-parent onto the new item: still valid
 * KMD, zero diagnostics, quietly the wrong diagram.
 */
const subtreeEnd = (lines: Line[], index: number): number => {
  const base = indentOf(lines[index].text).length;
  let last = index;
  for (let i = index + 1; i < lines.length; i++) {
    const value = lines[i].text;
    if (isBlank(value)) {
      continue;
    }
    if (!isListItem(value) || indentOf(value).length <= base) {
      break;
    }
    last = i;
  }
  return last;
};

/**
 * The container heading the caret belongs to.
 *
 * Scans upward first — the enclosing container. With the caret above every
 * heading (the common case right after the panel seeds a document, when the
 * caret is still at offset 0), it falls FORWARD to the first heading instead
 * of giving up: appending to the end of the document would drop the new item
 * into the last container rather than the first, which is never what pressing
 * a button on an untouched document means.
 */
const containerHeadingLine = (
  lines: Line[],
  caretLine: number,
): number | null => {
  for (let i = caretLine; i >= 0; i--) {
    if (isHeading(lines[i].text)) {
      return i;
    }
  }
  for (let i = caretLine + 1; i < lines.length; i++) {
    if (isHeading(lines[i].text)) {
      return i;
    }
  }
  return null;
};

/**
 * The two most recent things an edge statement can legally reference, for
 * pre-filling. Family-aware on purpose: in `er` and `sequence` the referencable
 * entities are the HEADINGS (entities, participants) — a field row or a list
 * item is not addressable, and pre-filling one produces KMD020.
 */
const recentRefs = (
  lines: Line[],
  caretLine: number,
  family: Family,
): string[] => {
  const headingsOnly = family === "er" || family === "sequence";
  const strip = (raw: string) =>
    raw
      .replace(/\s*\{[^}]*\}\s*$/, "")
      .replace(/^[^:]{1,30}:\s+/, "")
      .trim();

  const refs: string[] = [];
  for (let i = caretLine; i >= 0 && refs.length < 2; i--) {
    const value = lines[i].text;
    const heading = HEADING_RE.exec(value);
    if (heading) {
      refs.push(strip(heading[2]));
      continue;
    }
    if (!headingsOnly) {
      const item = LIST_RE.exec(value);
      if (item) {
        refs.push(strip(item[2]));
      }
    }
  }
  // scan forward too — a fresh document may have everything below the caret
  for (let i = caretLine + 1; i < lines.length && refs.length < 2; i++) {
    const heading = HEADING_RE.exec(lines[i].text);
    if (heading) {
      refs.push(strip(heading[2]));
    }
  }
  return refs.reverse();
};

// ---------------------------------------------------------------------------
// snippets
// ---------------------------------------------------------------------------

/**
 * Sentinel wrapping the placeholder text the caret should select. A single
 * control character: it cannot occur in anything a user typed, and both marks
 * are stripped before the result is returned, so it never reaches the document.
 */
const MARK = "\u0001";

const snippetFor = (
  action: ActionId,
  family: Family,
  labels: string[],
): {
  lines: string[];
  placement: "sibling" | "child" | "container" | "document";
} => {
  const container = FAMILY_INFO[family].container;
  const [from, to] = [labels[0] ?? "A", labels[1] ?? labels[0] ?? "B"];

  switch (action) {
    case "container":
      return {
        lines: ["", `## ${MARK}New ${container}${MARK}`, ""],
        placement: "container",
      };
    case "subcontainer":
      return {
        lines: ["", `### ${MARK}New subsystem${MARK}`, ""],
        placement: "container",
      };
    case "node":
      return { lines: [`- ${MARK}New step${MARK}`], placement: "sibling" };
    case "start":
      return { lines: [`- ${MARK}Start${MARK} {start}`], placement: "sibling" };
    case "end":
      return { lines: [`- ${MARK}End${MARK} {end}`], placement: "sibling" };
    case "decision":
      return {
        lines: [
          `- ${MARK}Condition?${MARK} {decision}`,
          `  - yes: Then this`,
          `  - no: Or this`,
        ],
        placement: "sibling",
      };
    case "branch":
      return {
        lines: [`- ${MARK}label${MARK}: Then this`],
        placement: "child",
      };
    case "child":
      return { lines: [`- ${MARK}New child${MARK}`], placement: "child" };
    case "field":
      return {
        lines: [`- ${MARK}name${MARK}: text`],
        placement: "sibling",
      };
    case "event":
      return { lines: [`- ${MARK}New event${MARK}`], placement: "sibling" };
    case "note":
      return { lines: [`> ${MARK}A note${MARK}`], placement: "sibling" };
    case "sectionBreak":
      return { lines: ["", "---", ""], placement: "container" };
    case "edge":
      return {
        lines: [`${MARK}${from}${MARK} -> ${to}`],
        placement: "document",
      };
    case "reply":
      return {
        lines: [`${MARK}${from}${MARK} ..> ${to}: reply`],
        placement: "document",
      };
    case "selfMessage":
      return {
        lines: [`${MARK}${from}${MARK} -> ${from}: internal step`],
        placement: "document",
      };
    case "relationship":
      return {
        lines: [`${MARK}${from}${MARK} -- ${to}: relates {1:0..n}`],
        placement: "document",
      };
    case "group":
    case "loop":
    case "opt":
    case "alt":
    case "par": {
      // a directive frame holds whatever its family holds: sequence frames
      // wrap MESSAGES (list items are KMD031 there), flow and architecture
      // frames wrap items
      const body =
        family === "sequence" ? `${from} -> ${to}: message` : "- Step";
      const separator = DIRECTIVES[action]?.separator;
      const lines = ["", `:::${action}[${MARK}Label${MARK}]`, body];
      if (separator) {
        lines.push(`::${separator}[Other]`, body);
      }
      lines.push(":::", "");
      return { lines, placement: "container" };
    }
    default:
      return { lines: [`- ${MARK}New${MARK}`], placement: "sibling" };
  }
};

// ---------------------------------------------------------------------------
// the insert
// ---------------------------------------------------------------------------

export const applyAction = (
  text: string,
  caret: number,
  action: ActionId,
  family: Family,
): InsertResult => {
  const lines = toLines(text);
  const body = bodyStartLine(lines);
  const current = lineAt(lines, Math.max(0, Math.min(caret, text.length)));
  // a caret parked in frontmatter behaves as if it were at the top of the body
  const caretLine = Math.max(current.index, body);

  const { lines: snippet, placement } = snippetFor(
    action,
    family,
    recentRefs(lines, caretLine, family),
  );

  let insertAfter: number;
  let indent = "";

  switch (placement) {
    case "document":
      insertAfter = lines.length - 1;
      break;
    case "container": {
      const heading = containerHeadingLine(lines, caretLine);
      insertAfter =
        heading === null ? lines.length - 1 : blockEnd(lines, heading);
      break;
    }
    case "child": {
      insertAfter = caretLine;
      indent = `${indentOf(lines[caretLine].text)}  `;
      break;
    }
    case "sibling":
    default: {
      if (isListItem(lines[caretLine].text)) {
        insertAfter = subtreeEnd(lines, caretLine);
        indent = indentOf(lines[caretLine].text);
      } else {
        const heading = containerHeadingLine(lines, caretLine);
        insertAfter =
          heading === null ? lines.length - 1 : blockEnd(lines, heading);
      }
      break;
    }
  }

  const indented = snippet.map((line) => (line ? indent + line : line));
  const before = lines.slice(0, insertAfter + 1).map((l) => l.text);
  const after = lines.slice(insertAfter + 1).map((l) => l.text);

  // avoid stacking blank lines when the snippet already brings its own
  while (indented[0] === "" && before[before.length - 1]?.trim() === "") {
    indented.shift();
  }
  while (
    indented[indented.length - 1] === "" &&
    (after[0] === undefined || after[0].trim() === "")
  ) {
    indented.pop();
  }

  const merged = [...before, ...indented, ...after].join("\n");
  const markStart = merged.indexOf(MARK);
  const cleaned = merged.split(MARK).join("");

  if (markStart === -1) {
    return {
      text: cleaned,
      selectionStart: cleaned.length,
      selectionEnd: cleaned.length,
    };
  }
  const markEnd = merged.indexOf(MARK, markStart + 1);
  return {
    text: cleaned,
    selectionStart: markStart,
    selectionEnd: markEnd === -1 ? markStart : markEnd - 1,
  };
};

// ---------------------------------------------------------------------------
// per-family catalog
// ---------------------------------------------------------------------------

/**
 * Which buttons a family offers. Derived from `FAMILY_INFO` and `DIRECTIVES`
 * wherever the vocabulary already answers the question, so a language change
 * cannot leave the UI advertising something the compiler rejects.
 */
export const buttonsFor = (family: Family): ButtonSpec[] => {
  const info = FAMILY_INFO[family];
  const out: ButtonSpec[] = [];
  const add = (id: ActionId, labelKey: string) => out.push({ id, labelKey });

  const outline = family === "mindmap" || family === "tree";

  add("container", `btn_container_${family}`);
  if (info.maxHeadingDepth >= 3 && !outline) {
    add("subcontainer", "btn_subcontainer");
  }

  if (family === "er") {
    add("field", "btn_field");
  } else if (family === "timeline") {
    add("event", "btn_event");
  } else if (family === "sequence") {
    // participants are the containers; messages are the edges
  } else {
    add("node", outline ? "btn_child_sibling" : "btn_node");
  }

  if (outline) {
    add("child", "btn_child");
  }

  if (family === "flow" || family === "swimlane") {
    add("decision", "btn_decision");
    add("branch", "btn_branch");
    add("start", "btn_start");
    add("end", "btn_end");
  }

  // edges: every family has them, but the useful default differs
  if (family === "sequence") {
    add("edge", "btn_message");
    add("reply", "btn_reply");
    add("selfMessage", "btn_self");
  } else if (family === "er") {
    add("relationship", "btn_relationship");
  } else {
    add("edge", outline ? "btn_crosslink" : "btn_edge");
  }

  if (info.sectionBreak) {
    add("sectionBreak", "btn_sectionBreak");
  }

  // directives come straight from the vocabulary
  for (const [name, spec] of Object.entries(DIRECTIVES)) {
    if (spec.families.includes(family)) {
      add(name as ActionId, `btn_${name}`);
    }
  }

  if (!outline) {
    add("note", "btn_note");
  }

  return out;
};

/**
 * A brand-new document of the given family. Each one is shaped the way that
 * family actually works — a sequence has no list items (messages are edges), an
 * ER entity has typed field rows — so the very first thing the editor puts on
 * screen compiles with zero errors and zero warnings.
 */
const STARTER_BODY: Record<Family, string> = {
  flow: "## First stage\n- First step\n- Second step\n",
  swimlane: "## First lane\n- First step\n\n## Second lane\n- Second step\n",
  architecture: "## First zone\n- First component {service}\n",
  sequence: "## Browser {actor}\n## API\n\nBrowser -> API: first message\n",
  mindmap: "# Central idea\n\n## First branch\n- First child\n",
  tree: "# Root\n\n## First branch\n- First child\n",
  er: "## Customer\n- id: uuid {pk}\n- email: text\n",
  timeline: "## 2026\n- First event\n",
};

export const starterFor = (family: Family): string =>
  `---\nkhiyarsa: 1\ntype: ${family}\n---\n\n${STARTER_BODY[family]}`;
